import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { UserAvatar } from '~/components/user-avatar/user-avatar.component'
import { Tooltip, makeStyles, IconButton, Fade } from '@material-ui/core'
import { Check } from '@material-ui/icons'
import { CopySvg } from '~/components/icons'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { PATHS } from '~/common/constants'

const WrapperBox = styled.div`
	cursor: pointer;
	display: flex;
	width: 100%;
	align-items: center;
`

const TitleLink = styled(Link)`
	margin-left: 10px;
	color: ${({ color }) => color || 'inherit'};
	text-decoration: none;
	&:hover {
		text-decoration: ${({ disableClick }) => (disableClick ? 'none' : 'underline')};
	}
`

const useStyles = makeStyles((theme) => ({
	title: {
		...theme.typography.title1,
		width: '100%',
	},
	fullName: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: 'calc(100% - 35px)',
	},
	email: {
		...theme.typography.body1,
		color: '#333335',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		flex: '0 1 auto', // Allow email to shrink but don't grow
		minWidth: 0, // Allow text to shrink below content size
	},
	emailContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '2px',
		width: 'fit-content',
		maxWidth: '100%',
		minWidth: 0, // Allow flex item to shrink
	},
}))

export const DEFAULT_AVATAR = process.env.PUBLIC_URL + '/assets/care/default-worker-avatar.png?version=' + process.env.REACT_APP_CI_COMMIT_TAG

export const UserTitle = ({ disableClick, data, color, useForJobApplicant = false, jobApplicant, onClick, ...rest }) => {
	const classes = useStyles()
	const location = useLocation()
	const { t } = useTranslation()
	const desLink = useForJobApplicant ? `${PATHS.recruiter.job_applicants}/${jobApplicant?.id}` : `${PATHS.recruiter.candidate_info}/${data?.id}`
	
	const [copied, setCopied] = useState(false)
	const [isEmailHovered, setIsEmailHovered] = useState(false)
	const timeoutRef = useRef()

	const handleCopyEmail = async (e) => {
		e.stopPropagation()
		e.preventDefault()
		if (!data?.email) return
		
		try {
			await navigator.clipboard.writeText(data.email)
			setCopied(true)
			clearTimeout(timeoutRef.current)
			timeoutRef.current = setTimeout(() => {
				setCopied(false)
			}, 1000)
		} catch (err) {
			console.error('Failed to copy email: ', err)
		}
	}

	const EmailWithCopy = ({ children }) => (
		<div 
			className={classes.emailContainer}
			onMouseEnter={() => setIsEmailHovered(true)}
			onMouseLeave={() => setIsEmailHovered(false)}
		>
			<div className={classes.email}>{children}</div>
			{data?.email && (
				<Fade in={isEmailHovered || copied} timeout={280}>
					<span style={{ flexShrink: 0 }}> {/* Prevent copy button from shrinking */}
						<Tooltip
							title={copied ? t('COPIED') : t('COPY')}
							placement="top"
							arrow
						>
							<IconButton
								size="small"
								onClick={handleCopyEmail}
								style={{
									padding: '2px',
									marginLeft: '-2px',
									color: copied ? '#388e3c' : isEmailHovered ? '#1976d2' : '#bdbdbd',
									transition: 'color 0.5s',
									display: 'flex',
									alignItems: 'center',
									background: 'none',
								}}
							>
								{copied ? (
									<Check fontSize="small" style={{ width: 14, height: 14 }} />
								) : (
									<CopySvg style={{ width: 14, height: 14 }} />
								)}
							</IconButton>
						</Tooltip>
					</span>
				</Fade>
			)}
		</div>
	)
	
	return (
		<WrapperBox onClick={() => onClick?.()} {...rest}>
			<UserAvatar customWidth={32} customHeight={32} fontSize={16} firstName={data?.firstName} lastName={data?.lastName} src={data?.avatar?.file?.fileUrl} />
			<Tooltip
				title={
					<div>
						<div>{data?.fullName}</div>
						<div>{data?.email}</div>
					</div>
				}
				placement="bottom-start"
			>
				{disableClick ? (
					<div className={classes.title} style={{ color, marginLeft: 10 }}>
						<div className={classes.fullName}>{data?.fullName}</div>
						<EmailWithCopy>{data?.email}</EmailWithCopy>
					</div>
				) : (
					<TitleLink
						className={classes.title}
						color={color}
						disableClick={disableClick}
						to={{
							pathname: desLink,
							state: { from: { pathname: location.pathname, search: location.search } }
						}}
					>
						<div className={classes.fullName}>{data?.fullName}</div>
						<EmailWithCopy>{data?.email}</EmailWithCopy>
					</TitleLink>
				)}
			</Tooltip>
		</WrapperBox>
	)
}
